import { useState, useEffect, useRef } from "react";
import { Link, useNavigate } from "react-router-dom";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";

// ── Real Logic Imports (from File 2) ──────────────────────────────────────────
import useNumericInput from "../../hooks/useNumericInput";
import useAlphabeticInput from "../../hooks/useAlphabeticInput";
import CartItemsinCheckout from "../canvas/CartItemsinCheckout";
import { useCart } from "../../context/CartContext";
import Breadcrump from "../../components/Breadcrump";
import Summary from "./common/Summary";
import addressAPI from "../../api/addressApi";
import { useCheckout } from "../../hooks/useCheckout";
import { showError, showSuccess } from "../../utils/toastUtils";
import PaymentService from "./gateways/PaymentServices";
import { useSiteConfig } from "../../context/SiteConfigContext";

gsap.registerPlugin(ScrollTrigger);


// ─── Icons (exact from File 1) ────────────────────────────────────────────────
const ArrowRight = () => (
  <svg className="co-arrow" viewBox="0 0 12 12" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M1 6h10M7 1l5 5-5 5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const LockIcon = () => (
  <svg width="11" height="11" viewBox="0 0 11 11" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="1" y="5" width="9" height="6" rx="1"/>
    <path d="M3 5V3.5a2.5 2.5 0 0 1 5 0V5" strokeLinecap="round"/>
  </svg>
);
const CheckIcon = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none" stroke="currentColor" strokeWidth="1.5">
    <path d="M6 14l5.5 5.5L22 9" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);
const CardIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="1" y="3" width="14" height="10" rx="2"/><path d="M1 7h14"/>
  </svg>
);
const PhoneIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="4" y="1" width="8" height="14" rx="2"/>
    <circle cx="8" cy="12.5" r="0.8" fill="currentColor"/>
  </svg>
);
const BankIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <path d="M2 6l6-4 6 4v1H2V6z"/>
    <line x1="3" y1="7" x2="3" y2="13"/>
    <line x1="6" y1="7" x2="6" y2="13"/>
    <line x1="10" y1="7" x2="10" y2="13"/>
    <line x1="13" y1="7" x2="13" y2="13"/>
    <line x1="1" y1="13" x2="15" y2="13"/>
  </svg>
);
const CodIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.3">
    <rect x="1" y="4" width="14" height="10" rx="1"/>
    <path d="M5 4V3a3 3 0 0 1 6 0v1"/>
    <circle cx="8" cy="9" r="2"/>
  </svg>
);

// Payment code → icon map
const PAYMENT_ICON = { razorpay: CardIcon, cod: CodIcon, payu: BankIcon, upi: PhoneIcon };

// ─── Component ────────────────────────────────────────────────────────────────
export default function Checkout() {

  // ── File 2: hooks ──
  useNumericInput();
  useAlphabeticInput();

  const { cartItems, fetchCart, cartSummary } = useCart();
  const { config } = useSiteConfig();
  const navigate = useNavigate();

  // ── File 1: animation refs ──
  const eyebrowRef = useRef(null);
  const headingRef = useRef(null);
  const stepsRef   = useRef(null);
  const formColRef = useRef(null);
  const summaryRef = useRef(null);

  // ── File 2: state ──
  const [errors, setErrors]                   = useState({});
  const [loading, setLoading]                 = useState(false);
  const [active, setActive]                   = useState("contact");
  const [savedAddresses, setSavedAddresses]   = useState([]);
  const [selectedAddress, setSelectedAddress] = useState("");

  // ── File 2: payment methods (commented ones preserved as in original) ──
  const paymentMethods = [
    // { code: "cod",  name: "Cash on Delivery" },
    { code: "razorpay", name: "Pay with Razorpay" },
    // { code: "payu", name: "Pay with PayU" },
  ];

  // ── File 2: auth + initial form data ──
  const auth = (() => {
    try { return JSON.parse(localStorage.getItem("token") ?? "{}"); }
    catch { return {}; }
  })();

  const INITIAL_FORM_DATA = {
    email:         auth.email || sessionStorage.getItem("emailId") || "",
    phone:         sessionStorage.getItem("phoneNum") || "",
    firstName:     "",
    lastName:      "",
    address: {
      addressType:  "home",
      addressLine1: "",
      addressLine2: "",
      pinCode:      "",
      district:     "",
      state:        "",
      country:      "",
    },
    paymentMethod: "razorpay",
  };

  const [formData, setFormData] = useState(INITIAL_FORM_DATA);
  const { placeOrder }          = useCheckout(cartItems, formData);

  // ── File 2: derived ──
  const emailRegex     = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const phoneRegex     = /^[6-9]\d{9}$/;
  const isContactValid = !!formData.email?.trim() && !!formData.phone?.trim();

  // ─── File 2: handleChange ─────────────────────────────────────────────────
  const handleChange = (e) => {
    const { name, value } = e.target;
    setSelectedAddress("");
    if (name.includes(".")) {
      const [parent, child] = name.split(".");
      setFormData((prev) => ({ ...prev, [parent]: { ...prev[parent], [child]: value } }));
    } else {
      setFormData((prev) => ({ ...prev, [name]: value }));
    }
  };

  // ─── File 2: contact validation ──────────────────────────────────────────
  const validateContact = () => {
    const errs = {};
    if (!formData.email)                       errs.email = "Please Enter Email Address";
    else if (!emailRegex.test(formData.email)) errs.email = "Please enter a valid email";
    if (!formData.phone)                       errs.phone = "Enter Phone Number";
    else if (!phoneRegex.test(formData.phone)) errs.phone = "Phone must be of 10 digits";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── File 2: address validation ──────────────────────────────────────────
  const validateAddress = () => {
    const requiredFields = ["firstName","addressType","addressLine1","pinCode","district","state","country"];
    const errs = {};
    requiredFields.forEach((field) => {
      if (field === "firstName") {
        if (!formData.firstName) errs[field] = `Please Enter ${field}`;
      } else {
        if (!formData.address[field]) errs[field] = `Please Enter ${field}`;
      }
    });
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  // ─── File 2: session storage ──────────────────────────────────────────────
  const saveSessionData = () => {
    const { email, phone } = formData;
    email ? sessionStorage.setItem("emailId", email)  : sessionStorage.removeItem("emailId");
    phone ? sessionStorage.setItem("phoneNum", phone) : sessionStorage.removeItem("phoneNum");
  };

  // ─── File 2: handleContinue ───────────────────────────────────────────────
  const handleContinue = (e) => {
    e.preventDefault();
    saveSessionData();
    if (!validateContact()) return;
    setActive("address");
    getAllMyAddress();
  };

  // ─── File 2: fetch saved addresses ───────────────────────────────────────
  const getAllMyAddress = async () => {
    try {
      const res = await addressAPI.fetchAllAddress({ email: formData.email });
      if (res.code === 200) {
        setSavedAddresses(res.data);
        setFormData((prev) => ({
          ...prev,
          firstName: res.data.firstName || "",
          lastName:  res.data.lastName  || "",
        }));
      }
    } catch (_) {}
  };

  // ─── File 2: select saved address ─────────────────────────────────────────
  const handleSavedAddressSelect = (address) => {
    setSelectedAddress(address._id);
    setFormData((prev) => ({
      ...prev,
      firstName: address.firstName || "",
      lastName:  address.lastName  || "",
      address: {
        addressType:  address.addressType  || "home",
        addressLine1: address.addressLine1 || "",
        addressLine2: address.addressLine2 || "",
        pinCode:      address.pinCode      || "",
        district:     address.district     || "",
        state:        address.state        || "",
        country:      address.country      || "",
      },
    }));
  };

  // ─── File 2: pincode auto-fill useEffect ─────────────────────────────────
  useEffect(() => {
    if (formData.address?.pinCode?.length === 6) {
      fetchPincodeDetails(formData.address.pinCode);
    }
  }, [formData.address?.pinCode]);

  const fetchPincodeDetails = async (pincode) => {
    try {
      const res  = await fetch(`https://api.postalpincode.in/pincode/${pincode}`);
      const data = await res.json();
      if (data[0].Status === "Success") {
        const po = data[0].PostOffice[0];
        setFormData((prev) => ({
          ...prev,
          address: { ...prev.address, state: po.State, district: po.District, country: po.Country },
        }));
      }
    } catch (err) { console.error("Pincode error:", err); }
  };

  // ─── File 2: auto-advance for returning users ─────────────────────────────
  useEffect(() => {
    if (formData.email && formData.phone && validateContact()) {
      getAllMyAddress();
      setActive("address");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── File 2: handlePlaceOrder ─────────────────────────────────────────────
  const handlePlaceOrder = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (!validateAddress()) { setLoading(false); return; }
      const action = await placeOrder();
      if (!action) throw new Error("Something went wrong");

      if (action.type === "COD_SUCCESS") {
        const { orderId } = action.payload;
        showSuccess(action.message);
        sessionStorage.setItem("orderId", orderId);
        sessionStorage.removeItem("emailId");
        sessionStorage.removeItem("phoneNum");
        navigate("/order-confirmation");
      }

      if (action.type === "PAYMENT_STARTED") {
        const { orderId, paymentMethod, gatewayOrderId, amount, currency } = action.payload;
        PaymentService.pay(paymentMethod.toLowerCase(), {
          gatewayOrderId, orderId, amount, currency, formData, navigate, config, fetchCart,
        });
      }
    } catch (err) {
      console.error(err);
      showError(err.message || "Order failed");
    } finally {
      setLoading(false);
    }
  };

  // ─── File 1: GSAP entrance ────────────────────────────────────────────────
  useEffect(() => {
    const ctx = gsap.context(() => {
      const tl = gsap.timeline({ defaults: { ease: "power3.out" } });
      tl
        .to(eyebrowRef.current, { opacity: 1, duration: 0.65 }, 0)
        .to(headingRef.current, { opacity: 1, y: 0, duration: 0.85 }, 0.18)
        .to(stepsRef.current,   { opacity: 1, duration: 0.55 }, 0.38)
        .to(formColRef.current, { opacity: 1, duration: 0.7  }, 0.48)
        .to(summaryRef.current, { opacity: 1, x: 0, duration: 0.7 }, 0.55);
    });
    return () => ctx.revert();
  }, []);

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <>
      {/* File 2: Breadcrumb */}
      

      <main className="checkout-page" aria-label="Checkout">
        <Breadcrump title="Checkout" />
        <div className="container">

          {/* ────────────────────────────────────────────────────────
              HEADER — File 1 exact UI
          ──────────────────────────────────────────────────────── */}
          <div className="checkout-header">
            <p className="checkout-eyebrow" ref={eyebrowRef}>
              <span className="checkout-eyebrow-line" aria-hidden="true"/>
              Secure Checkout
            </p>
            <h1 className="checkout-heading" ref={headingRef}>
              Complete Your <em>Order</em>
            </h1>
            <div className="checkout-steps" ref={stepsRef} aria-label="Checkout steps">
              {[
                { label: "Cart",     state: "done"   },
                { label: "Checkout", state: "active" },
                { label: "Confirm",  state: ""       },
              ].map((step, i) => (
                <div key={step.label} style={{ display: "flex", alignItems: "center" }}>
                  <div className={`checkout-step ${step.state}`}>
                    <span className="checkout-step-dot" aria-hidden="true"/>
                    {step.label}
                  </div>
                  {i < 2 && <div className="checkout-step-line" aria-hidden="true"/>}
                </div>
              ))}
            </div>
          </div>

          <div className="row g-4 g-lg-5">

            {/* ════════════════════════════════════════════════════════
                LEFT COLUMN — Accordion Forms
                UI:    File 1 (section titles, labels, inputs, button)
                Logic: File 2 (state, validation, handlers, pincode)
            ════════════════════════════════════════════════════════ */}
            <div className="col-12 col-lg-7 checkout-form-col" ref={formColRef}>

              {/* ── ACCORDION 1: Contact Details ── */}
              <div className="checkout-accordion-wrap">
                <div
                  className={`checkout-accordion-head ${active === "contact" ? "is-active" : ""}`}
                  role="button"
                  aria-expanded={active === "contact"}
                  onClick={() => {
                    /* File 2 guard: block back-nav if contact invalid */
                    if (active === "address" && !isContactValid) return;
                    setActive("contact");
                  }}
                >
                  <span className="checkout-accordion-title">
                    Contact Details
                    {/* File 2: show filled values as chip when collapsed */}
                    {active === "address" && isContactValid && (
                      <span className="checkout-accordion-filled">
                        {formData.email} · {formData.phone}
                      </span>
                    )}
                  </span>
                  <span className="checkout-accordion-chevron" aria-hidden="true"/>
                </div>

                <div className={`checkout-accordion-body ${active === "contact" ? "is-open" : ""}`}>
                  {/* File 2: onSubmit → handleContinue */}
                  <form onSubmit={handleContinue} noValidate>
                    <div className="row g-3">

                      {/* Email
                          File 1 UI: checkout-label + checkout-input styling
                          File 2 logic: name="email", value={formData.email}, onChange={handleChange} */}
                      <div className="col-12 col-sm-7 checkout-form-group">
                        <label className="checkout-label" htmlFor="co-email">
                          Email Address
                        </label>
                        <input
                          id="co-email"
                          type="email"
                          name="email"
                          className={`checkout-input${errors.email ? " has-error" : ""}`}
                          placeholder="you@example.com"
                          value={formData.email}
                          onChange={handleChange}
                          autoComplete="email"
                        />
                        {errors.email && <span className="checkout-field-error">{errors.email}</span>}
                      </div>

                      {/* Phone
                          File 1 UI: checkout-label + checkout-input styling
                          File 2 logic: name="phone", only-numeric class, maxLength=10 */}
                      <div className="col-12 col-sm-5 checkout-form-group">
                        <label className="checkout-label" htmlFor="co-phone">
                          Phone Number
                        </label>
                        <input
                          id="co-phone"
                          type="text"
                          name="phone"
                          className={`checkout-input only-numeric${errors.phone ? " has-error" : ""}`}
                          placeholder="+91 98765 43210"
                          value={formData.phone}
                          onChange={handleChange}
                          maxLength={10}
                          autoComplete="tel"
                        />
                        {errors.phone && <span className="checkout-field-error">{errors.phone}</span>}
                      </div>

                    </div>
                    {/* File 1 UI: checkout-continue-btn | File 2: type="submit" → handleContinue */}
                    <button type="submit" className="checkout-continue-btn">
                      <span>Continue</span>
                      <ArrowRight/>
                    </button>
                  </form>
                </div>
              </div>

              {/* ── ACCORDION 2: Shipping Address ── */}
              <div className="checkout-accordion-wrap">
                <div
                  className={`checkout-accordion-head ${active === "address" ? "is-active" : ""}`}
                  role="button"
                  aria-expanded={active === "address"}
                  onClick={() => {
                    /* File 2 guard: block access until contact is valid */
                    if (!isContactValid) return;
                    setActive("address");
                  }}
                >
                  <span className="checkout-accordion-title">Shipping Address</span>
                  <span className="checkout-accordion-chevron" aria-hidden="true"/>
                </div>

                <div className={`checkout-accordion-body ${active === "address" ? "is-open" : ""}`}>

                  {/* ── File 2: Saved Addresses ──
                      savedAddresses comes from addressAPI.fetchAllAddress()
                      Each addr has: _id, addressType, firstName, lastName,
                        addressLine1, addressLine2, district, state, pinCode, country */}
                  {savedAddresses.length > 0 && (
                    <div style={{ marginBottom: "1.6rem" }}>
                      <span className="checkout-saved-addr-heading">Saved Addresses</span>
                      {savedAddresses.map((addr) => (
                        <label
                          key={addr._id}
                          className="checkout-saved-addr-card"
                          onClick={() => handleSavedAddressSelect(addr)}
                        >
                          <input
                            type="radio"
                            name="savedAddress"
                            checked={selectedAddress === addr._id}
                            onChange={() => handleSavedAddressSelect(addr)}
                          />
                          <div>
                            <p className="checkout-saved-addr-badge">{addr.addressType}</p>
                            <p className="checkout-saved-addr-text">
                              {addr.firstName} {addr.lastName}<br/>
                              {addr.addressLine1}
                              {addr.addressLine2 ? `, ${addr.addressLine2}` : ""}<br/>
                              {addr.district}, {addr.state} — {addr.pinCode}<br/>
                              {addr.country}
                            </p>
                          </div>
                        </label>
                      ))}
                      <h2 className="checkout-section-title" style={{ marginTop: "1.4rem" }}>
                        Or Enter New Address
                      </h2>
                    </div>
                  )}

                  {/* Name row
                      File 2: name="firstName" / name="lastName"
                      File 2: only-alphabets class on both */}
                  <div className="row g-3">
                    <div className="col-12 col-sm-6 checkout-form-group">
                      <label className="checkout-label" htmlFor="co-fname">
                        First Name <span className="req">*</span>
                      </label>
                      <input
                        id="co-fname"
                        type="text"
                        name="firstName"
                        className={`checkout-input only-alphabets${errors.firstName ? " has-error" : ""}`}
                        placeholder="Priya"
                        value={formData.firstName}
                        onChange={handleChange}
                        autoComplete="given-name"
                      />
                      {errors.firstName && <span className="checkout-field-error">{errors.firstName}</span>}
                    </div>
                    <div className="col-12 col-sm-6 checkout-form-group">
                      <label className="checkout-label" htmlFor="co-lname">Last Name</label>
                      <input
                        id="co-lname"
                        type="text"
                        name="lastName"
                        className="checkout-input only-alphabets"
                        placeholder="Sharma"
                        value={formData.lastName}
                        onChange={handleChange}
                        autoComplete="family-name"
                      />
                    </div>
                  </div>

                  {/* Address Type
                      File 1 UI: pill-style labels
                      File 2: name="address.addressType" dot-notation, values: home/work/other */}
                  <div className="checkout-form-group">
                    <label className="checkout-label">
                      Address Type <span className="req">*</span>
                    </label>
                    <div className="checkout-addr-type-row">
                      {["home", "work", "other"].map((type) => (
                        <label key={type} className="checkout-addr-type-pill">
                          <input
                            type="radio"
                            name="address.addressType"
                            value={type}
                            checked={formData.address.addressType === type}
                            onChange={handleChange}
                          />
                          {type.charAt(0).toUpperCase() + type.slice(1)}
                        </label>
                      ))}
                    </div>
                    {errors.addressType && <span className="checkout-field-error">{errors.addressType}</span>}
                  </div>

                  {/* Street Address — File 2: name="address.addressLine1" */}
                  <div className="checkout-form-group">
                    <label className="checkout-label" htmlFor="co-addr1">
                      Street Address <span className="req">*</span>
                    </label>
                    <input
                      id="co-addr1"
                      type="text"
                      name="address.addressLine1"
                      className={`checkout-input${errors.addressLine1 ? " has-error" : ""}`}
                      placeholder="123, Rajpath, Connaught Place"
                      value={formData.address.addressLine1}
                      onChange={handleChange}
                      autoComplete="street-address"
                    />
                    {errors.addressLine1 && <span className="checkout-field-error">{errors.addressLine1}</span>}
                  </div>

                  {/* Apartment — File 2: name="address.addressLine2" (optional) */}
                  <div className="checkout-form-group">
                    <label className="checkout-label" htmlFor="co-addr2">
                      Apartment / Suite
                      <span className="checkout-label-optional">(Optional)</span>
                    </label>
                    <input
                      id="co-addr2"
                      type="text"
                      name="address.addressLine2"
                      className="checkout-input"
                      placeholder="Flat 4B, Tower A"
                      value={formData.address.addressLine2}
                      onChange={handleChange}
                      autoComplete="address-line2"
                    />
                  </div>

                  {/* Pincode / District / State
                      File 2: pinCode triggers fetchPincodeDetails → auto-fills district/state/country
                      File 2: only-numeric on pinCode, only-alphabets on district+state */}
                  <div className="row g-3">
                    <div className="col-12 col-sm-4 checkout-form-group">
                      <label className="checkout-label" htmlFor="co-pin">
                        Postal Code <span className="req">*</span>
                      </label>
                      <input
                        id="co-pin"
                        type="text"
                        name="address.pinCode"
                        className={`checkout-input only-numeric${errors.pinCode ? " has-error" : ""}`}
                        placeholder="110001"
                        value={formData.address.pinCode}
                        onChange={handleChange}
                        maxLength={6}
                        autoComplete="postal-code"
                      />
                      {errors.pinCode && <span className="checkout-field-error">{errors.pinCode}</span>}
                    </div>

                    <div className="col-12 col-sm-4 checkout-form-group">
                      <label className="checkout-label" htmlFor="co-district">
                        District <span className="req">*</span>
                      </label>
                      <input
                        id="co-district"
                        type="text"
                        name="address.district"
                        className={`checkout-input only-alphabets${errors.district ? " has-error" : ""}`}
                        placeholder="New Delhi"
                        value={formData.address.district}
                        onChange={handleChange}
                      />
                      {errors.district && <span className="checkout-field-error">{errors.district}</span>}
                    </div>

                    <div className="col-12 col-sm-4 checkout-form-group">
                      <label className="checkout-label" htmlFor="co-state">
                        State <span className="req">*</span>
                      </label>
                      <input
                        id="co-state"
                        type="text"
                        name="address.state"
                        className={`checkout-input only-alphabets${errors.state ? " has-error" : ""}`}
                        placeholder="Delhi"
                        value={formData.address.state}
                        onChange={handleChange}
                      />
                      {errors.state && <span className="checkout-field-error">{errors.state}</span>}
                    </div>
                  </div>

                  {/* Country — File 2: name="address.country", auto-filled by pincode */}
                  <div className="checkout-form-group">
                    <label className="checkout-label" htmlFor="co-country">
                      Country <span className="req">*</span>
                    </label>
                    <input
                      id="co-country"
                      type="text"
                      name="address.country"
                      className={`checkout-input only-alphabets${errors.country ? " has-error" : ""}`}
                      placeholder="India"
                      value={formData.address.country}
                      onChange={handleChange}
                      autoComplete="country-name"
                    />
                    {errors.country && <span className="checkout-field-error">{errors.country}</span>}
                  </div>

                </div>
              </div>

            </div>
            {/* end form col */}


            {/* ════════════════════════════════════════════════════════
                RIGHT COLUMN — Order Summary Card
                UI:    File 1 (sticky card, heading, section titles, button)

                CartItemsinCheckout (File 2 component):
                  item.thumbnail  → <figure><img/></figure>
                  item.title      → <p className="name">
                  item.variantName→ <span className="variant">
                  item.quantity   → <span className="variant">Qty:<b>
                  item.price      → price shown as ₹price/-
                  item.mrp        → strikethrough if mrp > price
                  item.subtotal   → shown below price
                  Restyled via .checkout-items-list CSS above

                Summary (File 2 component):
                  cartSummary.subtotal  → "Subtotal After Discount"
                  cartSummary.total     → grand total
                  cartSummary.discount  → coupon discount (if > 0)
                  cartSummary.shipping  → shipping charge
                  cartItems[].mrp * qty → totalWithoutDiscount (strikethrough)
                  Restyled via .checkout-summary-card .list-total CSS above

                Payment: File 1 grid + File 2 paymentMethods + handleChange
                Place Order: File 1 button + File 2 handlePlaceOrder + loading
            ════════════════════════════════════════════════════════ */}
            <div className="col-12 col-lg-5">
              <div className="checkout-summary-card" ref={summaryRef}>

                <h2 className="checkout-summary-title">
                  Order Summary
                  {cartItems?.length > 0 && (
                    <span className="checkout-summary-count">
                      {cartItems.length} item{cartItems.length !== 1 ? "s" : ""}
                    </span>
                  )}
                </h2>

                {/* CartItemsinCheckout — File 2 component
                    item._id        → key
                    item.thumbnail  → img src (via image inside component)
                    item.title      → product name
                    item.variantName→ variant label
                    item.quantity   → qty display
                    item.price      → selling price
                    item.mrp        → original price (strikethrough)
                    item.subtotal   → line total */}
                {cartItems && cartItems.length > 0 && (
                  <ul className="checkout-items-list">
                    {cartItems.map((item) => (
                      <CartItemsinCheckout item={item} key={item._id} />
                    ))}
                  </ul>
                )}

                {/* Summary — File 2 component
                    cartSummary.subtotal  → after-discount subtotal
                    cartSummary.total     → grand total
                    cartSummary.discount  → coupon savings
                    cartSummary.shipping  → shipping cost
                    reduces cartItems to get totalWithoutDiscount (MRP sum) */}
                <Summary cartItems={cartItems} cartSummary={cartSummary} />

                <div style={{
                  borderTop: "1px solid var(--border-light, #e2d9d0)",
                  margin: "1.2rem 0",
                }}/>

                {/* Payment Methods
                    File 1 UI: checkout-payment-grid + checkout-payment-option pills
                    File 2 logic: paymentMethods array, name="paymentMethod", handleChange */}
                <h3 className="checkout-payment-section-title">Payment Method</h3>
                <div className="checkout-payment-grid" role="radiogroup" aria-label="Payment method">
                  {paymentMethods.map(({ code, name }) => {
                    const Icon = PAYMENT_ICON[code] || CardIcon;
                    return (
                      <label key={code} className="checkout-payment-option">
                        <input
                          type="radio"
                          name="paymentMethod"
                          value={code}
                          className="checkout-payment-radio"
                          checked={formData.paymentMethod === code}
                          onChange={handleChange}
                        />
                        <span className="checkout-payment-icon" aria-hidden="true"><Icon/></span>
                        <span className="checkout-payment-label">{name}</span>
                      </label>
                    );
                  })}
                </div>

                {/* Place Order Button
                    File 1 UI: checkout-place-btn with slide-reveal hover
                    File 2 logic: onClick={handlePlaceOrder}, disabled={loading}
                    Loading: File 1 spinner style (not bootstrap) */}
                <button
                  className="checkout-place-btn"
                  onClick={handlePlaceOrder}
                  disabled={loading}
                  aria-label="Place order"
                >
                  {loading ? (
                    <>
                      <span className="checkout-place-spinner" aria-hidden="true"/>
                      <span>Processing…</span>
                    </>
                  ) : (
                    <>
                      <span>Place Order</span>
                      <ArrowRight/>
                    </>
                  )}
                </button>

                <p className="checkout-secure-note">
                  <LockIcon/> Secured with 256-bit SSL encryption
                </p>

              </div>
            </div>

          </div>
        </div>
      </main>
    </>
  );
}